import { useEffect, useState } from 'react';
import { HyruleToken } from '@nintendo/hyrule-react-commons';
import { gql, useLazyQuery } from '@apollo/client';
import { GamePlayDataModel } from './GamePlayDataModel';
import Utils from '../../../shared/Utils';
import { GameDetails, GameSummaryApiModel } from '../../../api/models';
import { GamePlaySummaryApi } from '../../../api';

export const NCOM_GRAPH_BATCH_SIZE = 25;

interface GamePlayHookData {
    data: GamePlayDataModel[];
    loading: boolean;
    error: boolean;
    optedOut: boolean;
}

export type PRODUCT_BY_APPID_QUERY = {
    request: {
        query: typeof NCOM_PRODUCT_BY_APP_ID;
        variables: {
            where: {
                appId: {
                    in: string[];
                };
            };
        };
    };
    result: {
        data: {
            storeProducts: [
                {
                    name: string;
                    sku: string;
                    appId: string;
                    productImage: {
                        url: string;
                    };
                },
            ];
        };
    };
};

export const NCOM_PRODUCT_BY_APP_ID = gql`
    query StoreProducts($where: ProductFilterInput) {
        storeProducts(where: $where) {
            name
            appId
            productImage {
                url
            }
        }
    }
`;

export const useGamePlay = (
    naid: string,
    token: HyruleToken,
    correlationId: string,
): GamePlayHookData => {
    const [data, setData] = useState<GamePlayDataModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [optedOut, setOptedOut] = useState<boolean>(false);

    const [getGameDetails] = useLazyQuery<{
        storeProducts: GameDetails[];
    }>(NCOM_PRODUCT_BY_APP_ID);

    const getGamePlayData = async (): Promise<void> => {
        setLoading(true);
        try {
            const gameHistorySummaries: GameSummaryApiModel =
                await GamePlaySummaryApi.getGameHistorySummaries(naid, token, correlationId);

            if (gameHistorySummaries.optedOut) {
                setOptedOut(gameHistorySummaries.optedOut);
            } else {
                const { playedTitles } = gameHistorySummaries;
                const batches = [];

                for (let i = 0; i < playedTitles.length; i += NCOM_GRAPH_BATCH_SIZE) {
                    const batch = playedTitles
                        .slice(i, i + NCOM_GRAPH_BATCH_SIZE)
                        .map((game) => game.titleId);
                    batches.push(batch);
                }

                const promises = batches.map((batch) =>
                    getGameDetails({
                        variables: {
                            where: {
                                appId: {
                                    in: batch,
                                },
                            },
                        },
                    }),
                );

                const results = await Promise.all(promises);
                const allGameDetails: GameDetails[] = results
                    .map((result) => result.data?.storeProducts ?? [])
                    .flat();

                setData(
                    GamePlaySummaryApi.mapToGamePlayDataModelList(
                        gameHistorySummaries,
                        naid,
                        allGameDetails,
                    ),
                );
            }

            setError(false);
            setLoading(false);
        } catch (error) {
            setData([]);
            setError(true);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (Utils.isValidNAID(naid) && token.accessToken) {
            getGamePlayData();
        } else {
            setData([]);
        }
    }, [naid, token]);

    return {
        data,
        loading,
        error,
        optedOut,
    };
};
